import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip, IconButton, MenuItem } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { formatPhoneNumber } from '~/utils/helperFunctions';

// Redux + hooks
import { useDispatch, useSelector } from 'react-redux';
import { resendInviteEmail, getTeamMembersData, deleteMember } from '~/redux/actions/user-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { displayTooltip } from '~/hooks/displayTooltip';

// Components
import EditUserModal from '~/components/Account/User/EditUserModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import TruncatedText from '~/components/General/TruncatedText';

// Utils
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { USER_ROLES, USER_STATUS, DEFAULT_PAGE_SIZES } from '~/utils/constants';
import { useMediaQuery } from '@mui/material';

// Icons
import { ResendCIcon, DeleteSettingCIcon, ModalTrashIcon, GeneralEditIcon } from '~/assets/images';

const UserTable = ({ pagination, updatePagination, initialLoad }) => {
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down(1350));

  const [user, setSelectedUser] = useState(null);
  const [editUserModal, setEditUserModal] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    icon: '',
    loading: false,
    handleOk: () => {},
    handleClose: () => {},
    successButtonName: '',
    cancelButtonName: 'No',
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const open = Boolean(anchorEl);
  const isTooltip = displayTooltip();

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setActiveRowId(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setActiveRowId(null);
    }, 200);
  };

  const fetchedRows = useSelector((state) => state.userReducers.teamMembers.rows) || [];
  const to = useSelector((state) => state.userReducers.teamMembers.to);
  const from = useSelector((state) => state.userReducers.teamMembers.from);
  const lastPage = useSelector((state) => state.userReducers.teamMembers.lastPage || 1);
  const currentPage = useSelector((state) => state.userReducers.teamMembers.currentPage || 1);
  const perPage =
    useSelector((state) => state.userReducers.teamMembers.perPage) || pagination.pageSize;
  const isLoading = useSelector((state) => state.userReducers.loading);
  const rowsCount = useSelector((state) => state.userReducers.teamMembers.count) || 0;
  const currentUser = useSelector((state) => state.userReducers.user);
  const rows = fetchedRows;
  const tableLoading = initialLoad || isLoading || paginationLoading;

  const pageSizes = DEFAULT_PAGE_SIZES;

  useEffect(() => {
    setPaginationLoading(false);
  }, [fetchedRows]);

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    updatePagination({ page: newPage, pageSize: pagination.pageSize });
  };

  const resendInvite = async (userId) => {
    const response = await dispatch(resendInviteEmail(userId));
    if (response.status === 200) dispatch(success(response.data?.message));
    else dispatch(failure(response.data?.message));
  };

  const deleteUser = async (userId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(deleteMember(userId));
      if (response.status === 200) {
        dispatch(success(response?.data?.message));
        dispatch(getTeamMembersData(pagination));
        setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
      } else {
        dispatch(failure(response?.data?.message));
        setConfirmModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const columns = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => {
        const name = `${params.row.firstName} ${params.row.lastName}`;
        const displayText = params.row.status === 'Invited' ? '-' : name;
        return <TruncatedText text={displayText} />;
      },
    },
    {
      field: 'email',
      headerName: 'Email Address',
      width: 250,
      compactShrinkMinWidth: 140,
      compactShrinkPriority: 1,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 1 : 0,
      renderCell: (params) => <TruncatedText text={params.row.email} />,
    },
    {
      field: 'phoneNo',
      headerName: MESSAGES.SIGN_UP.INPUT_LABEL_PHONE,
      width: 180,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 0,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 1 : 0,
      renderCell: (params) => <TruncatedText text={formatPhoneNumber(params.row.phoneNo) || '-'} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: isSmallScreen ? 160 : 100,
      compactShrinkMinWidth: 95,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => {
        const statusText = USER_STATUS[params.row.status?.toUpperCase()];
        return <TruncatedText text={statusText || ''} />;
      },
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 120,
      compactShrinkMinWidth: 80,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => (
        <Typography>
          {USER_ROLES.find((r) => params.row.role?.slug === r.value)?.title || '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => <Typography>{dateFormat(params.row.createdAt, true)}</Typography>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const actionsAvailable =
          currentUser.id !== params.row.id &&
          currentUser.isOrgAdmin &&
          !params.row.isSystemAdmin &&
          !params.row.isOrganizationOwner;

        const isMenuOpen = open && activeRowId?.id === params.row.id;

        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <Tooltip
              title={!actionsAvailable ? 'Actions not available' : ''}
              placement="left"
              disableInteractive
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -15],
                      },
                    },
                  ],
                },
              }}
            >
              <span>
                <IconButton
                  aria-label={`Open actions for ${params.row.firstName || params.row.email} ${params.row.lastName || ''}`.trim()}
                  onClick={(e) => actionsAvailable && handleMenuClick(e, params.row)}
                  disabled={!actionsAvailable}
                  className="olcTableDropdownIcon"
                  sx={{
                    opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows]);

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (!tableLoading && rows.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [tableLoading, rows.length]);

  return (
    <Box
      className={`olcTableGridInternal ${tableLoading ? 'table-loading' : ''} ${rows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnMenu
          disableRowSelectionOnClick
          pagination={true}
          rowHeight={61}
          shrinkColumnsToFit
          loading={tableLoading}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      {(tableLoading || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => {
            setPaginationLoading(true);
            updatePagination({ page: 1, pageSize: event.target.value });
          }}
          from={from}
          to={to}
          total={rowsCount}
          loading={tableLoading}
        />
      )}

      {/* Menu Actions */}
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {activeRowId?.status === 'Invited' && (
          <MenuItem
            onClick={() => {
              resendInvite(activeRowId?.id);
              handleMenuClose();
            }}
          >
            <img src={ResendCIcon} alt="resend" />
            Resend
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setSelectedUser(activeRowId);
            setEditUserModal(true);
            handleMenuClose();
          }}
        >
          <img src={GeneralEditIcon} alt="edit" />
          Change Role
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmModal({
              open: true,
              message: MESSAGES.ACCOUNT.USERS.DELETE_USER.HEADING,
              header: MESSAGES.ACCOUNT.USERS.DELETE_USER.TITLE,
              icon: ModalTrashIcon,
              loading: false,
              handleOk: () => deleteUser(activeRowId?.id),
              handleClose: () =>
                setConfirmModal((prev) => ({
                  ...prev,
                  open: false,
                })),
              successButtonName: MESSAGES.ACCOUNT.USERS.DELETE_USER.SUBMIT_BUTTON,
              cancelButtonName: MESSAGES.ACCOUNT.USERS.DELETE_USER.CANCEL_BUTTON,
            });
            handleMenuClose();
          }}
        >
          <img src={DeleteSettingCIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>

      {/* Modals */}
      <EditUserModal
        openModal={editUserModal}
        handleCloseModal={() => setEditUserModal(false)}
        selectedUser={user}
        pagination={pagination}
      />
      <ConfirmModal openConfirmModal={confirmModal} />
    </Box>
  );
};

export default UserTable;
